import { Dispatch, SetStateAction } from "react";

export default class AudioRecorder {
  // Variable containing the recorder object
  private recorder: MediaRecorder | null;
  private stream: MediaStream | null;
  private handleData: ((e: BlobEvent) => void) | null;

  constructor() {
    this.recorder = null;
    this.stream = null;
    this.handleData = null;
  }

  async startRecording({
    setStartTime,
    setIsRecording,
    setError,
    clearError,
    restart,
    onSuccess,
  }: StartRecordingProps) {
    setStartTime && setStartTime(Date.now());
    clearError && clearError();

    // For when user has already started recording, but paused the recording.
    // We just need to resume the recorder in this case.
    if (this.recorder && this.recorder?.state === "paused") {
      return this.recorder.resume();
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);

    this.recorder = recorder;
    this.stream = stream;

    recorder.start();

    recorder.addEventListener("start", () => setIsRecording(true));
    recorder.addEventListener("resume", () => setIsRecording(true));

    const handleData = (e: BlobEvent) => {
      const audio = e.data;

      const audioCtx = new AudioContext();
      const source = audioCtx.createBufferSource();

      const reader = new FileReader();
      reader.readAsArrayBuffer(audio);
      reader.onload = () => {
        const audioCtx = new AudioContext();
        audioCtx
          .decodeAudioData(reader.result as ArrayBuffer)
          .then((buffer) => {
            // perform audio analysis here
            source.buffer = buffer;
            const data = buffer.getChannelData(0);
            let max = 0;
            let min = 0;

            for (let i = 0; i < data.length; i++) {
              max = Math.max(max, data[i]);
              min = Math.min(min, data[i]);
            }
            const amplitude = (max - min) / 2;

            if (amplitude < 0.1) {
              // The audio is silent
              setError &&
                setError(
                  <>
                    No audio detected <br />
                    Please ensure your microphone is working
                  </>
                );
              restart && restart();
              this.recorder = null;
            } else {
              // The audio has actual sound
              onSuccess(audio);

              this.recorder = null;
            }
          });
      };
    };

    this.handleData = handleData;

    // Save recorded audio in local state when the recording stops
    recorder.addEventListener("dataavailable", handleData);
  }

  pauseRecording({
    startTime,
    setIsRecording,
    setIsPaused,
    setPausedTime,
  }: PauseRecordingProps) {
    setIsRecording(false);
    this.recorder && this.recorder.pause();

    let pTime = Date.now() - startTime;
    pTime = pTime / 1000;
    pTime = pTime % 1; // Get the decimal only from the number Ex: 3.88 => .88 (We only need the decimal)

    setIsPaused && setIsPaused(true);
    setPausedTime && setPausedTime(pTime);
  }

  finishRecording({ setPausedTime, setIsPaused }: FinishRecordingProps) {
    this.recorder && this.recorder.stop();
    this.stream && this.stream.getTracks().forEach((track) => track.stop());

    this.recorder = null;
    this.stream = null;

    setIsPaused && setIsPaused(false);
    setPausedTime && setPausedTime(null);
  }

  cancelRecording() {
    // Remove Event Listener that handles Audio Data
    if (this.recorder && this.handleData) {
      this.recorder.removeEventListener("dataavailable", this.handleData);
    }

    // This removes the recording icon form the Browser tab.
    this.stream && this.stream.getTracks().forEach((track) => track.stop());

    this.stream = null;
    this.recorder = null;
  }
}

interface StartRecordingProps {
  setStartTime?: Dispatch<SetStateAction<number | null>>;
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  setError?: (error: any) => void;
  clearError?: () => void;
  restart?: () => void;
  onSuccess: (audio: Blob) => void;
}

interface PauseRecordingProps {
  setPausedTime?: Dispatch<SetStateAction<number | null>>;
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  setIsPaused?: Dispatch<SetStateAction<boolean>>;
  startTime: number;
}

interface FinishRecordingProps {
  setPausedTime?: Dispatch<SetStateAction<number | null>>;
  setIsPaused?: Dispatch<SetStateAction<boolean>>;
}
